import React from 'react';
import { Bell, Calendar } from 'lucide-react';
import { Database } from '../types/supabase';

type Note = Database['public']['Tables']['notes']['Row'];

interface ReminderSectionProps {
  notes: Note[];
  navigate: (to: string) => void;
}

export default function ReminderSection({ notes, navigate }: ReminderSectionProps) {
  // Filter notes with pending reminders and sort by reminder time
  const upcomingReminders = notes
    .filter(note => 
      note.reminder_at &&
      note.reminder_status === 'pending' &&
      new Date(note.reminder_at) > new Date() // Only show future reminders
    )
    .sort((a, b) => 
      new Date(a.reminder_at!).getTime() - new Date(b.reminder_at!).getTime()
    );

  if (upcomingReminders.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Bell className="h-5 w-5 mr-2 text-indigo-500" />
          Reminders
        </h2>
        <button
          onClick={() => navigate('/notes')}
          className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
        >
          View All Notes
        </button>
      </div>

      <div className="space-y-4">
        {upcomingReminders.map((note) => {
          const reminderDate = new Date(note.reminder_at!);
          const now = new Date();
          const diffTime = reminderDate.getTime() - now.getTime();
          const diffMinutes = Math.floor(diffTime / (1000 * 60));
          const diffHours = Math.floor(diffMinutes / 60);
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
          
          let timeStatus;
          if (diffMinutes < 60) {
            timeStatus = `In ${diffMinutes} minutes`;
          } else if (diffHours < 24) {
            timeStatus = `In ${diffHours} hours`;
          } else if (diffDays === 1) {
            timeStatus = 'Tomorrow';
          } else {
            timeStatus = `In ${diffDays} days`;
          }

          return (
            <div
              key={note.id}
              className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
            >
              <div className="flex-shrink-0">
                <Calendar className="h-5 w-5 text-gray-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  {note.title}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {note.content}
                </p>
                <div className="mt-2 flex items-center space-x-4">
                  <span className={`text-xs font-medium ${
                    diffMinutes < 60 ? 'text-red-600 dark:text-red-400' :
                    diffHours < 24 ? 'text-orange-600 dark:text-orange-400' :
                    'text-indigo-600 dark:text-indigo-400'
                  }`}>
                    {timeStatus}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {reminderDate.toLocaleString(undefined, {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}